import invariant from 'invariant';

export class Permissions {
    static async load(fetcher) {
        // Mithrandir links have trailing slash
        try {
            const { response } = await fetcher.get('/ticket/_self/');
            return new this(response.roles);
        } catch {
            return new this([]);
        }
    }

    constructor(roles) {
        invariant(roles instanceof Array, `Bad roles ${roles}`);
        this._roles = roles;
    }

    get capacities() {
        return new Set(
            this._roles
                .map(role => role.role.capacities)
                .flat()
                .map(capacity => capacity.slug)
        );
    }

    get roles() {
        return this._roles.map(role => role.role.slug);
    }

    hasRole(role) {
        return this._roles.find(r => r.role.slug === role.toLowerCase());
    }

    isAdmin() {
        return this._roles.some(r => r.role.slug === 'admin' && r.context.level === 0);
    }

    hasCapacity(capacity, organization_id) {
        return (
            this._roles
                // lookup match roles
                .filter(r => r.context.level === 0 || (organization_id && r.context.level === 3 && r.context.value === organization_id.toString()))
                // find at least one role with the capacity
                .some(r => r.role.capacities.find(c => c.slug === capacity))
        );
    }

    rootContext(role) {
        return role.context.level === 0;
    }

    capacityOrga(organization_id) {
        return new Set(
            this._roles
                .filter(r => this.rootContext(r) || (r.context.level === 3 && r.context.value === organization_id.toString()))
                .flatMap(role => role.role.capacities.map(capacity => capacity.slug))
        );
    }

    capacityFlow(organization_id, flowId) {
        return new Set(
            this._roles
                .filter(r => this.rootContext(r) || (r.context.level === 5 && (r.context.value === flowId.toString() || r.context.parents[1].value === organization_id.toString())))
                .flatMap(role => role.role.capacities.map(capacity => capacity.slug))
        );
    }

    hasRightsOnOrga(organization_id) {
        // To check if user has right on this orga
        // 1 - We check on roles that are not for minisite (all minisite are on the new bo)
        // 2 - Check only on role who have [TICKET] in, to exclude all role like [TARGET] who also have right on this orga but not for ticket
        // 3 - get the first context-level and if user have rights, context value should match the organization_id

        // TODO: Remove the role.name check - permission checks should not be based on role names.
        return this._roles.some(
            r =>
                r.role.slug !== 'minisite' &&
                (r.role.name.toUpperCase().includes('[TICKET]') || r.role.slug === 'owner') &&
                r.context.level === 3 &&
                r.context.value === organization_id.toString()
        );
    }

    hasPermissionsCrossOrga() {
        //TODO : Remove filter on slug when nBO is open
        return this._roles.some(r => r.context.level === 0 && r.role.slug !== 'minisite');
    }

    canAccessNBO(organization_id) {
        const isAdmin = this.isAdmin();
        const hasPermissionsCrossOrga = this.hasPermissionsCrossOrga();
        const hasRightsOnOrga = this.hasRightsOnOrga(organization_id);

        return isAdmin || hasPermissionsCrossOrga || hasRightsOnOrga;
    }

    hasCapacities(capacities, organizationId) {
        const userCapacities = this.capacityOrga(organizationId);
        return capacities.find(capacity => userCapacities.has(capacity));
    }

    isModerationAdmin(organizationId) {
        return Boolean(this.hasCapacities(['PyvarCapacity.MODERATION_ADMIN'], organizationId));
    }
}
